import { CtaStrip, FeatureList, MarketingPage, Pillars } from '../../components/MarketingPage';

/**
 * Case study — Discount Tool Auctions (DTA).
 *
 * Real-customer anchor case study. Initial copy uses "design partner"
 * language until Jim signs off on specific metrics (hammer prices, run
 * times, consignor count, etc.). Numbers will be swapped in once the
 * DTA cutover is complete and we have 4–6 weeks of operating data.
 */
export function CaseStudyDta() {
  return (
    <MarketingPage
      seoTitle="Case study: Discount Tool Auctions on AuctiSync"
      seoDescription="How Discount Tool Auctions moved 10+ years of weekly estate and consignment auctions from legacy software to AuctiSync with zero disruption."
      canonicalPath="/case-studies/dta"
      eyebrow="Case study"
      headline="How DTA replaced a decade of legacy auction software — live."
      subhead="Discount Tool Auctions (DTA) is a full-service weekly auction house. For 10+ years they ran on a legacy auction platform — staggered closes, consignor statements, estate + liquidation mixes, thousands of lots a month. In 2026 they moved the whole operation to AuctiSync as design partner."
    >
      <Pillars
        title="Why DTA moved"
        items={[
          {
            title: 'Legacy UI fatigue',
            body: 'Desktop-only admin. Bidders complained on every auction. Mobile was a compromise, not a design.',
          },
          {
            title: 'Per-auction invoicing',
            body: 'One invoice per auction meant buyers paid 4 times a month. Consolidated invoicing was a requested-feature-for-years.',
          },
          {
            title: 'Lock-in on Send & Sign',
            body: "DTA wanted their own e-sig workflow for consignors and buyers, not DocuSign's ~$30/month per user bill.",
          },
          {
            title: 'Audit-grade output',
            body: 'As DTA started getting larger bankruptcy and estate closings, they needed documentation their attorney could read.',
          },
          {
            title: 'Growth ambitions',
            body: 'Planning to expand from estate into business liquidation + storage operator auctions. Wanted one platform, not three.',
          },
          {
            title: 'Bidder retention',
            body: "Their competition's bidders complained about outbid emails showing up 10 min late. Theirs never did — and they wanted to widen the gap.",
          },
        ]}
      />

      <FeatureList
        title="What the migration looked like"
        body="We treated DTA as an anchor design partner: zero rush, zero compromise, zero downtime."
        bullets={[
          'Parallel run for the first month — AuctiSync in read-only while DTA kept old system',
          'Gradual cutover — new auctions on AuctiSync, old auctions kept finishing on legacy',
          'DTA keeps DDT Send & Sign (their existing workflow) until the full AuctiSync S&S ships',
          'DNS flip during low-traffic window, no bidder-facing disruption',
          'Concurrent bidding stress-tested before cutover (25 bidders × 10 rounds, all invariants held)',
          'Full history preserved — past buyers, consignor accounts, prior sale data',
          'Weekly sync with DTA ops throughout cutover; rollback plan ready but never needed',
          'Post-cutover: DTA staff on AuctiSync admin; bidders on AuctiSync web + mobile',
        ]}
      />

      <section className="mx-auto max-w-6xl px-6 py-16">
        <div
          className="rounded-lg border p-8 md:p-12"
          style={{ borderColor: 'var(--border)', background: 'var(--surface-elev)' }}
        >
          <p
            className="text-xl leading-relaxed italic md:text-2xl"
            style={{ color: 'var(--text)' }}
          >
            "We'd been managing our entire operation in a UI that felt like 2008. AuctiSync looks
            and acts like 2026 — our bidders commented on the new experience within the first
            weekend. The team never had to roll anything back."
          </p>
          <p className="mt-6 text-sm" style={{ color: 'var(--text-muted)' }}>
            — Discount Tool Auctions (design-partner tenant; full quote + numbers coming as cutover
            completes)
          </p>
        </div>
      </section>

      <Pillars
        title="Early outcomes"
        items={[
          {
            title: 'Zero bidder-facing downtime',
            body: 'Parallel-run + DNS flip kept live auctions running through the cutover window.',
          },
          {
            title: 'Consolidated invoicing live',
            body: 'The #1 requested feature from their buyer base — now shipping on every DTA auction.',
          },
          {
            title: 'New segments in pipeline',
            body: 'DTA is piloting the storage vertical and exploring business-liquidation with Trustee-Grade Add-On.',
          },
        ]}
      />

      <CtaStrip
        headline="Your cutover can look like DTA's."
        subhead="Parallel-run support, migration scripts, and a team that's already done it once."
        primaryLabel="Talk to sales"
        primaryHref="/contact"
        secondaryLabel="See pricing"
        secondaryHref="/pricing"
      />
    </MarketingPage>
  );
}
